import { useEffect, useState, useRef } from "react";
import useAuth from "../../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import {
  clearRcAutoFilters,
  setRcAutoFilters,
} from "../../../redux/slices/rcAutoFilters";
import { useFilterNavigation } from "../../../hooks/useFilterNavigation";
import {
  setRcAutoPageNumber,
  setRcAutoRowsPerPage,
} from "../../../redux/slices/rcAutoPagination";

export const useData = () => {
  const dispatch = useDispatch();
  const filterData = useSelector((state) => state.rcAutoFilters);
  const { pageNumber, rowsPerPage } = useSelector(
    (state) => state.rcAutoPagination,
  );

  // Temp state for filters (the values currently in the input fields)
  // Initially loaded from Redux (in case we're returning from details)
  const [tempFilterData, setTempFilterData] = useState(filterData || {});

  const [filterOptions, setFilterOptions] = useState(null);
  const [filterByDate, setFilterByDate] = useState(null);
  const [dateFilterNumber, setDateFilterNumber] = useState(30);
  const [doughnutChartData, setDoughnutChartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);
  const [rcData, setRcData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTableChanging, setIsTableChanging] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [isRemovingFilters, setIsRemovingFilters] = useState(false);
  const [canClearFilters, setCanClearFilters] = useState(false);
  const [isApplyFiltersButtonDisabled, setIsApplyFiltersButtonDisabled] =
    useState(true);
  const { getRcData, getData } = useAuth();
  const pollingIntervalRef = useRef(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());

  // Sync temp state when Redux filters are restored or cleared
  useEffect(() => {
    setTempFilterData(filterData || {});
  }, [filterData]);

  // Use filter navigation hook to manage persistence based on navigation source
  useFilterNavigation(
    () => {
      // onRestoreFilters: Filters are already in Redux, hook just notifies us
      if (filterData) {
        setCanClearFilters(true);
      }
    },
    () => {
      // onClearFilters: Clear filters from Redux if not coming from details page
      dispatch(clearRcAutoFilters());
      setCanClearFilters(false);
    },
  );

  // Side Effect for data fetching
  useEffect(() => {
    setIsTableChanging(true);
    getRcData(
      rowsPerPage,
      pageNumber,
      null,
      filterByDate ? { ...filterData, aging: filterByDate } : filterData,
    )
      .then((response) => {
        if (response.status === 1) {
          setDoughnutChartData(response?.data?.graphCircleData);
          setBarChartData(response?.data?.graphBarData);
          setTotalRows(response?.data?.totalRows);
          setRcData(response?.data?.dataset);
          setLastUpdateTime(new Date());
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
        setIsTableChanging(false);
      });
  }, [pageNumber, rowsPerPage, filterData]); // Also trigger on filterData change (applied search)

  useEffect(() => {
    getData({ action: "rcAutoGetFilter" }).then((response) => {
      if (response.status === 1) {
        setFilterOptions(response.data);
      }
    });
  }, []);

  // Polling effect - refresh data every 30 seconds
  useEffect(() => {
    let timeoutId;

    const poll = async () => {
      // Only poll if not currently loading or changing filters
      if (
        !loading &&
        !isTableChanging &&
        !filtersLoading &&
        !isRemovingFilters
      ) {
        try {
          const response = await getRcData(
            rowsPerPage,
            pageNumber,
            null,
            filterByDate ? { ...filterData, aging: filterByDate } : filterData,
          );
          if (response.status === 1) {
            setDoughnutChartData(response?.data?.graphCircleData);
            setBarChartData(response?.data?.graphBarData);
            setTotalRows(response?.data?.totalRows);
            setRcData(response?.data?.dataset);
            setLastUpdateTime(new Date());
          }
        } catch (err) {
          console.log("Polling error:", err);
        }
      }
      // Schedule next poll
      timeoutId = setTimeout(poll, 30000);
    };

    // Start polling when component mounts and data is loaded
    if (!loading) {
      timeoutId = setTimeout(poll, 30000);
    }

    // Cleanup polling on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    loading,
    isTableChanging,
    filtersLoading,
    isRemovingFilters,
    rowsPerPage,
    pageNumber,
    filterData,
    filterByDate,
  ]);

  // Check if Search button should be enabled based on temp filters
  useEffect(() => {
    const hasActiveFilters = (data) => {
      if (!data) return false;
      return Object.values(data).some(
        (v) => v !== "" && v !== null && v !== undefined,
      );
    };

    setIsApplyFiltersButtonDisabled(!hasActiveFilters(tempFilterData));
    setCanClearFilters(hasActiveFilters(filterData));
  }, [tempFilterData, filterData]);

  useEffect(() => {
    filterByDate && handleApplyFilters();
  }, [filterByDate]);

  // function that handles the get rc list api call with the filter the user has set
  const handleApplyFilters = () => {
    setFiltersLoading(true);
    setIsTableChanging(true);
    setFilterByDate(null);

    // Save current temp filters to Redux
    dispatch(setRcAutoFilters(tempFilterData));
    dispatch(setRcAutoPageNumber(1)); // Reset to first page on search

    // The data fetch useEffect will handle the API call because filterData/pageNumber changed
    // But we still set loading state here
    setFiltersLoading(false);
  };

  // Function to be called before navigating to details to persist current filter state
  const handleSaveFilters = () => {
    dispatch(setRcAutoFilters(tempFilterData));
  };

  const handleRemoveFilters = () => {
    setIsTableChanging(true);
    dispatch(clearRcAutoFilters());
    dispatch(setRcAutoPageNumber(1));
    setIsRemovingFilters(true);

    // Reset local/temp state as well
    setTempFilterData({});

    getRcData(rowsPerPage, 1, null)
      .then((response) => {
        if (response.status === 1) {
          setDoughnutChartData(response?.data?.graphCircleData);
          setBarChartData(response?.data?.graphBarData);
          setTotalRows(response?.data?.totalRows);
          setRcData(response?.data?.dataset);
          setCanClearFilters(false);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsRemovingFilters(false);
        setIsTableChanging(false);
        setFilterByDate(null);
        setDateFilterNumber(30);
      });
  };

  return {
    handlePageChange: (newPage) =>
      !isTableChanging && dispatch(setRcAutoPageNumber(newPage + 1)),
    handlePageSizeChange: (newPageSize) => {
      dispatch(setRcAutoRowsPerPage(newPageSize));
      dispatch(setRcAutoPageNumber(1));
    },
    handleApplyFilters,
    handleRemoveFilters,
    handleSaveFilters, // New: to be used on item click
    doughnutChartData,
    barChartData,
    rcData,
    totalRows,
    loading,
    filtersLoading,
    isApplyFiltersButtonDisabled,
    pageNumber,
    rowsPerPage,
    filterOptions,
    filterData: tempFilterData, // Expose temp filters to the UI components
    setFilterData: setTempFilterData, // Update temp filters on input change
    setFilterByDate,
    canClearFilters,
    isRemovingFilters,
    dateFilterNumber,
    setDateFilterNumber,
    lastUpdateTime,
  };
};
